import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { UniButton } from '@/design-system/components/buttons';
import { HyperlinkButton } from '@/design-system/components/buttons/hyperlinkButton/HyperlinkButton';
import { getObjectScreenNameFromPath, popStateLockedMap } from '@/design-system/components/navigatorEmbed/UtxAppNavigatorEmbed';
import { H5 } from '@/design-system/components/typography';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { ScrollView, SxProp, View } from 'dripsy';
import * as DocumentPicker from 'expo-document-picker';
import _ from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { RootStackRoutes } from 'types';
import BillPaymentApp from '../..';
import { PAYMENT_TYPE, QuoteResponse, UseCreateBillPaymentProp } from '../../hooks/useCreateBillPayment.hook';
import { BillPaymentStackRoutes } from '../../payment.navigator';
import { mapUrlLink, onGoBack } from '../../utils/utils';
import { HelpCenterComponent } from '../components/HelpCenter.component';
import { UploadWebComponent } from './UploadWeb.component';

export interface BillPaymentInstructionWebProps {
  documentCallback: (file: DocumentPicker.DocumentResult) => void;
  useCreateBillPayment: UseCreateBillPaymentProp;
  emailComponent: JSX.Element;
}

export const BillPaymentInstructionWebComponent: React.FC<BillPaymentInstructionWebProps> = ({
  documentCallback,
  useCreateBillPayment,
  emailComponent
}) => {
  const { t } = useTranslation('global-bill-payment');
  const createBillPayment = useCreateBillPayment;
  const {
    general: { paymentType, alertMessage },
    instruction: { statusButtonQuote, setStatusButtonQuote, isLoadingUpload, isSuccessUpload, mapQuoteDataRequest, resetQuote, postQuote },
    postCreateBillPayment: { nextParamProp }
  } = createBillPayment;
  const navigation = useNavigation();

  useEffect(() => {
    if (!!nextParamProp && !!nextParamProp.request && nextParamProp.response) {
      if (statusButtonQuote) {
        if (!!nextParamProp.response && nextParamProp.response.classified) {
          postQuote(
            mapQuoteDataRequest(nextParamProp.request, nextParamProp.response),
            (data) => {
              const response: QuoteResponse = data.data;
              navigation.navigate(RootStackRoutes.BillPayment, {
                screen: BillPaymentStackRoutes.Confirmation,
                params: {
                  request: mapQuoteDataRequest(nextParamProp.request, response),
                  response: nextParamProp.response,
                  flagUploaded: false,
                  debitAccount: '',
                  documentSelected: createBillPayment.instruction.documentSelected,
                  paymentType: paymentType!
                }
              });
            },
            (error) => {
              console.log(error);
              navigation.navigate(RootStackRoutes.BillPayment, {
                screen: BillPaymentStackRoutes.Form,
                params: nextParamProp
              });
            }
          );
        } else {
          navigation.navigate(RootStackRoutes.BillPayment, {
            screen: BillPaymentStackRoutes.Form,
            params: nextParamProp
          });
        }
        setStatusButtonQuote(false);
      }
    }
  }, [nextParamProp, statusButtonQuote]);

  const onBack = () => {
    if (Platform.OS === 'web') {
      popStateLockedMap.set('popstate', true);
      onGoBack(navigation);
      setTimeout(() => popStateLockedMap.set('popstate', false), 500);
    } else {
      onGoBack(navigation);
    }
  };

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') {
        const onPopState = (event: any) => {
          window.history.pushState(event.state, '');
          setTimeout(() => {
            const popStateLocked = popStateLockedMap.get('popstate') ?? false;
            if (!document.location.href || popStateLocked) return;
            const { parentName, subName } = mapUrlLink(document.location.href);
            var screenNameObject = getObjectScreenNameFromPath([BillPaymentApp], `/${subName}`);
            if (
              screenNameObject.screenName &&
              screenNameObject.routeStackName === parentName &&
              screenNameObject.screenName !== BillPaymentStackRoutes.Landing
            ) {
              onBack();
            }
          }, 200);
        };
        window.addEventListener('popstate', onPopState);
        return () => {
          window.removeEventListener('popstate', onPopState);
        };
      }
    }, [])
  );

  return (
    <View sx={sxStyles.container}>
      <ScrollView sx={sxStyles.scrollView} showsVerticalScrollIndicator={false}>
        <View sx={sxStyles.body}>
          <View>
            <View sx={sxStyles.toolbar}>
              <HyperlinkButton
                icon="arrowLeft"
                iconPosition="left"
                iconSize={20}
                label={t('global-bill-payment::glossary.back-to-payment-overview')}
                onPress={() => onBack()}
              />
            </View>

            <View sx={sxStyles.viewRow}>
              <View sx={sxStyles.spaceX} />
              <H5 bold sx={sxStyles.title}>
                {paymentType === PAYMENT_TYPE.GLOBAL
                  ? t('global-bill-payment::glossary.title--overseas-bill-payment')
                  : t('global-bill-payment::glossary.title--local-bill-payment')}
              </H5>
            </View>
            <View sx={sxStyles.content}>
              <View sx={sxStyles.spaceX} />
              <View sx={sxStyles.leftComponent}>
                <UploadWebComponent documentCallback={documentCallback} />
                <View sx={sxStyles.containerContinue}>
                  <View sx={sxStyles.leftContainerContinue} />
                  <View sx={sxStyles.spaceContainerContinue} />
                  <View sx={sxStyles.continueButtonWrapper}>
                    {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={sxStyles.viewAlert} />}
                    {isSuccessUpload && !isLoadingUpload && (
                      <View sx={{ width: '100%' }}>
                        <UniButton
                          label={t('global-bill-payment::glossary.continue')}
                          onPress={async () => {
                            resetQuote();
                          }}
                          sx={sxStyles.button}
                          size="medium"
                          loading={statusButtonQuote}
                          disabled={statusButtonQuote}
                        />
                      </View>
                    )}
                  </View>
                  <View sx={sxStyles.spaceContainerContinue} />
                </View>
              </View>
              <View sx={sxStyles.space} />
              <View sx={sxStyles.rightComponent}>
                {emailComponent}
                <HelpCenterComponent sx={sxStyles.helpLayout} />
              </View>
              <View sx={sxStyles.spaceX} />
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: 'transparent',
    flex: 1
  },
  scrollView: {
    padding: '$3'
  },
  body: {
    backgroundColor: '$backgroundPrimary',
    flex: 1,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 10,
    overflow: 'hidden',
    borderRadius: '$radius3'
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: '$7',
    backgroundColor: '$backgroundSecondary'
  },
  textHeader: {
    color: '$primary',
    ml: '$2'
  },
  title: {
    flex: 142,
    pt: '$10',
    pb: '$5'
  },
  content: {
    pt: '$5',
    pb: '$19',
    flexDirection: 'row'
  },
  leftComponent: {
    flex: 78
  },
  space: {
    flex: 8,
    maxWidth: 80
  },
  spaceX: {
    flex: 12
  },
  rightComponent: {
    flex: 44,
    flexDirection: 'column'
  },
  footerLayout: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#DCDCDC'
  },
  helpLayout: {
    mt: '$10'
  },
  containerContinue: {
    backgroundColor: 'transparent',
    flexDirection: 'row',
    width: '100%',
    my: '$5'
  },
  leftContainerContinue: {
    flex: 29,
    pl: '$8',
    pr: '$1'
  },
  spaceContainerContinue: {
    flex: 4
  },
  continueButtonWrapper: {
    flex: 52,
    alignItems: 'center',
    zIndex: 1000
  },
  viewRow: {
    flexDirection: 'row'
  },
  button: {
    mt: '$5',
    width: '100%',
    height: '$11'
  }
};
